// Kaart in kaart (huisregel 4, owner 14-10: "als ik binnen een trend op een
// skill klik, wil ik de originele kaart daarbinnen zien; datzelfde voor
// funding of een bron; dit design pattern moet goed zijn").
//
// Eén mechanisme voor de hele nieuwe weergave:
// - `useOpenKaart()` geeft een functie; open({ soort, id }) legt een laag over
//   het paneel waarin je staat.
// - In die laag staat de ORIGINELE kaart van dat ding: precies de inhoud die
//   zijn eigen pagina in het paneel toont. Elke pagina levert die inhoud via
//   `registreerKaart(soort, Kaart)` in haar eigen `kaart.tsx`; nieuw/kaarten.ts
//   laadt die bestanden.
// - Vanuit een laag klik je verder (kaart in kaart in kaart): de laag schuift
//   door. "Terug" (ook op de eerste laag) en Esc gaan een stap terug, het kruis
//   sluit alle lagen. Maximaal drie diep.
//
// Een pagina bouwt dus nooit meer een eigen, kleinere versie van andermans kaart.

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

import { leesParam, useAdresParam, zetParam, zetParams } from "../lib/adres";
import { LegeStaat, Paneel } from "../stijl";

export type KaartSoort = "skill" | "trend" | "regeling" | "bron" | "laagtrend" | "ontwikkeling" | "bevinding";
export type KaartVerzoek = { soort: KaartSoort; id: string; /** Voor de kop zolang de kaart laadt. */ titel?: string };

/** Wat een kaart in de laag zet: dezelfde delen als het paneel op de eigen pagina. */
export type KaartDelen = { label: string; kop: ReactNode; inhoud: ReactNode; voet?: ReactNode };

/** De originele kaart van één soort. Haalt zelf op met `id` en geeft zijn delen
 *  aan `toon`; in de inhoud mag hij zelf weer `useOpenKaart()` gebruiken. */
export type KaartComponent = (p: { id: string; titel?: string; toon: (d: KaartDelen) => ReactNode }) => ReactNode;

const kaarten: Partial<Record<KaartSoort, KaartComponent>> = {};

/** Roep dit aan in de `kaart.tsx` van een pagina, op moduleniveau. */
export function registreerKaart(soort: KaartSoort, Kaart: KaartComponent): void {
  kaarten[soort] = Kaart;
}

const WOORD: Record<KaartSoort, string> = {
  skill: "Vaardigheid", trend: "Trend", regeling: "Regeling", bron: "Bron",
  laagtrend: "Trend", ontwikkeling: "Ontwikkeling", bevinding: "Bevinding",
};
const MAX = 3;

type Open = (k: KaartVerzoek) => void;
const Ctx = createContext<Open | null>(null);

/** Opent een originele kaart als laag. Buiten de nieuwe weergave doet hij niets. */
export function useOpenKaart(): Open {
  return useContext(Ctx) ?? (() => {});
}

const SOORTEN = Object.keys(WOORD) as KaartSoort[];

/** `?kaart=skill:12,trend:abc` -> stapel (onderaan eerst). Een ongeldige waarde valt weg. */
export function leesStapel(waarde: string | null): KaartVerzoek[] {
  if (!waarde) return [];
  const uit: KaartVerzoek[] = [];
  for (const deel of waarde.split(",")) {
    const i = deel.indexOf(":");
    if (i < 1) continue;
    const soort = deel.slice(0, i) as KaartSoort;
    if (!SOORTEN.includes(soort)) continue;
    let id = "";
    try { id = decodeURIComponent(deel.slice(i + 1)); } catch { continue; }
    if (id) uit.push({ soort, id });
  }
  return uit.slice(-MAX);
}
export function schrijfStapel(s: KaartVerzoek[]): string | null {
  return s.length ? s.map((k) => `${k.soort}:${encodeURIComponent(k.id)}`).join(",") : null;
}

/** Staat één keer in het kader; legt de lagen over alles wat eronder staat.
 *  De stapel staat in het adres (?kaart=soort:id): openen is een nieuwe stap in
 *  de geschiedenis, dus de terugknop van de browser sluit de bovenste laag. */
export function KaartLaag({ children }: { children: ReactNode }) {
  const [adres] = useAdresParam("kaart");
  const titels = useRef(new Map<string, string>());
  const stapel = useMemo(
    () => leesStapel(adres).map((k) => ({ ...k, titel: titels.current.get(`${k.soort}:${k.id}`) })),
    [adres],
  );
  // Hoeveel eigen stappen er in de geschiedenis boven je beginpunt liggen, staat in
  // history.state (sofKaart): het overleeft terug, vooruit en herladen, een teller niet.
  const diepte = (): number => {
    const n = (window.history.state as { sofKaart?: unknown } | null)?.sofKaart;
    return typeof n === "number" && n > 0 ? n : 0;
  };
  const open = useCallback<Open>((k) => {
    if (k.titel) titels.current.set(`${k.soort}:${k.id}`, k.titel);
    const nu = leesStapel(leesParam("kaart"));
    const zonder = nu.filter((x) => !(x.soort === k.soort && x.id === k.id));
    zetParams(
      { kaart: schrijfStapel([...zonder, { soort: k.soort, id: k.id }].slice(-MAX)) },
      "push",
      "kaart",
      { sofKaart: diepte() + 1 },
    );
  }, []);
  const terug = useCallback(() => {
    if (diepte() > 0) window.history.back();
    else zetParam("kaart", schrijfStapel(leesStapel(leesParam("kaart")).slice(0, -1)), "vervang");
  }, []);
  const sluit = useCallback(() => {
    const n = diepte();
    if (n > 0) {
      // Na de sprong terug kan er nog een kaart in het adres staan (herladen op ?kaart=…): die gaat ook weg.
      window.addEventListener("popstate", () => zetParam("kaart", null, "vervang"), { once: true });
      window.history.go(-n);
    } else zetParam("kaart", null, "vervang");
  }, []);
  const boven = stapel.at(-1) ?? null;
  const Kaart = boven ? kaarten[boven.soort] : undefined;
  const waarde = useMemo(() => open, [open]);

  const laag = (d: KaartDelen) => (
    // Owner 14-10: altijd een Terug-knop, ook op de eerste laag (dan terug naar het
    // paneel eronder). Terug en Esc gaan één laag terug; het kruis sluit alle lagen.
    <Paneel open niveau={2} onSluit={sluit} terug="Terug" onTerug={terug} label={d.label} kop={d.kop} voet={d.voet}>
      {d.inhoud}
    </Paneel>
  );

  return (
    <Ctx.Provider value={waarde}>
      {children}
      {boven &&
        (Kaart ? (
          <Kaart key={`${boven.soort}:${boven.id}`} id={boven.id} titel={boven.titel} toon={laag} />
        ) : (
          laag({
            label: `${WOORD[boven.soort]}: ${boven.titel ?? ""}`,
            kop: (
              <>
                <span className="s-meta">{WOORD[boven.soort]}</span>{" "}
                <strong>{boven.titel}</strong>
              </>
            ),
            inhoud: <LegeStaat zin="Deze kaart is nog niet aangesloten op de kaart-in-kaart." />,
          })
        ))}
    </Ctx.Provider>
  );
}

/** Kaart in kaart zonder overlay (dekking eindstand 25-10): de kaart staat op
 *  haar plek, bijvoorbeeld in een oud paneel of in de zoekoverlay, waar een
 *  laag van de kaartlaag achter dat paneel zou vallen. Een klik in de kaart
 *  (useOpenKaart) vervangt de kaart ter plekke; "Terug" gaat een stap terug.
 *  `toon` krijgt ook welke kaart boven ligt (sommige kaarten tonen hun titel zelf). */
export function KaartOpDePlek({
  begin,
  toon,
}: {
  begin: KaartVerzoek;
  toon: (d: KaartDelen, terug: (() => void) | null, boven: KaartVerzoek) => ReactNode;
}) {
  const [stapel, setStapel] = useState<KaartVerzoek[]>([begin]);
  const open = useCallback<Open>((k) => {
    setStapel((s) => [...s.filter((x) => !(x.soort === k.soort && x.id === k.id)), k].slice(-MAX));
  }, []);
  const terug = useCallback(() => setStapel((s) => (s.length > 1 ? s.slice(0, -1) : s)), []);
  const boven = stapel.at(-1) ?? begin;
  const Kaart = kaarten[boven.soort];
  const metTerug = stapel.length > 1 ? terug : null;
  return (
    <Ctx.Provider value={open}>
      {Kaart ? (
        <Kaart key={`${boven.soort}:${boven.id}`} id={boven.id} titel={boven.titel} toon={(d) => toon(d, metTerug, boven)} />
      ) : (
        toon({ label: boven.titel ?? WOORD[boven.soort], kop: <span className="s-meta">{WOORD[boven.soort]}</span>, inhoud: <LegeStaat zin="Deze kaart is hier nog niet aangesloten." /> }, metTerug, boven)
      )}
    </Ctx.Provider>
  );
}
